import React, { useEffect, useRef } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject as ChartInject,
  Legend,
  Category,
  ColumnSeries,
  LineSeries,
  DateTime,
  Tooltip,
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  AccumulationLegend,
  AccumulationTooltip,
  AccumulationDataLabel,
  AccumulationAnnotation,
  PieSeries,
  StackingColumnSeries,
} from "@syncfusion/ej2-react-charts";
import { QUDA_COLORS } from "../config/colors";

const ChartCard = ({ title, data, primaryXAxis, primaryYAxis, series, pieData, borderColor, borderWidth = 2, borderClass, pieTitle = "Order Types" }) => {
  const mainChartRef = useRef(null);
  const mainChartContainerRef = useRef(null);
  const appointmentChartRef = useRef(null);
  const appointmentChartContainerRef = useRef(null);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observers = [];

    const observeChart = (containerRef, chartRef) => {
      if (!containerRef.current) return;
      const observer = new ResizeObserver(() => {
        chartRef.current?.refresh();
      });
      observer.observe(containerRef.current);
      observers.push(observer);
    };

    observeChart(mainChartContainerRef, mainChartRef);
    observeChart(appointmentChartContainerRef, appointmentChartRef);

    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-4">
      {/* Main Chart */}
      <div
        ref={mainChartContainerRef}
        className={`col-span-1 lg:col-span-9 bg-white text-black shadow rounded-xl p-1 w-full flex flex-col ${borderClass || ''}`}
        style={{
          minHeight: 360,
          ...(borderClass ? {} : { border: `${borderWidth}px solid ${borderColor || QUDA_COLORS.TEAL_DARK}` }),
          borderRadius: "8px"
        }}
      >
        <h3 className="text-lg font-bold text-center mb-0 text-black">
          {title}
        </h3>
        <ChartComponent
          ref={mainChartRef}
          title=""
          titleStyle={{ fontSize: "18px", fontWeight: "bold", margin: "0px" }}
          primaryXAxis={primaryXAxis}
          primaryYAxis={primaryYAxis}
          axes={[
            {
              name: "RevenueAxis",
              title: "Revenue ($)",
              opposedPosition: true,
              labelFormat: "${value}",
              majorGridLines: { width: 0 },
            },
          ]}
          tooltip={{ enable: true }}
          height="100%"
          width="100%"
          legendSettings={{ visible: true }}
        >
          <ChartInject
            services={[
              ColumnSeries,
              StackingColumnSeries,
              LineSeries,
              Legend,

              DateTime,
              Tooltip,
            ]}
          />
          <SeriesCollectionDirective>
            {series.map((s, index) => (
              <SeriesDirective key={index} {...s} columnWidth={3} width={3} />
            ))}
          </SeriesCollectionDirective>
        </ChartComponent>
      </div>

      <div
        ref={appointmentChartContainerRef}
        className={`col-span-1 lg:col-span-3 bg-white text-black shadow rounded-xl p-2 flex flex-col ${borderClass || ''}`}
        style={{
          minHeight: 320,
          ...(borderClass ? {} : { border: `${borderWidth}px solid ${borderColor || QUDA_COLORS.TEAL_DARK}` }),
          borderRadius: "8px"
        }}
      >
        <h3 className="text-lg font-bold text-center mb-0 text-black">
          {pieTitle}
        </h3>
        <AccumulationChartComponent
          ref={appointmentChartRef}
          id="appointment-type-chart"
          width="100%"
          height="100%"
          enableSmartLabels={true}
          tooltip={{ enable: true }}
          legendSettings={{
            visible: true,
            position: "Bottom",
            shapeHeight: 20,
            shapeWidth: 20,
            toggleVisibility: false,
          }}
          tooltipRender={(args) => {
            const percentage = args.point.percentage.toFixed(2);
            args.text = `${args.point.x}: ${args.point.y} (${percentage}%)`;
          }}
          centerLabel={{
            text: "Total:\n" + pieData.reduce((sum, item) => sum + item.y, 0), 
            hoverTextFormat: "${point.x}:\n${point.y}",
            textStyle: {
              fontWeight: "600",
              size: "16px",
              color: "#333",
            },
          }}
        >
          <ChartInject
            services={[
              PieSeries,
              AccumulationLegend,
              AccumulationTooltip,
              AccumulationDataLabel,
              AccumulationAnnotation
            ]}
          />
          <AccumulationSeriesCollectionDirective>
            <AccumulationSeriesDirective
              dataSource={pieData.map((item) => ({
                ...item,
              }))}
              xName="x"
              yName="y"
              radius="100%"
              startAngle="270"
              endAngle="90"
              pointColorMapping="color"
              type="Pie"
              innerRadius="40%"
              explode={false}
              explodeOffset="5%"
              explodeIndex={1}
              border={{
                width: 2,
                color: "white",
              }}
              
              borderRadius={8}
              dataLabel={{
                visible: false,
              }}
            />
          </AccumulationSeriesCollectionDirective>
        </AccumulationChartComponent>
      </div>
    </div>
  );
};

export default ChartCard;
